// ADENDA z65/z66 (Diego, 30-oct-2026) · Multisitio por sitio, ETAPA 1 (Cotizador/MULTISITIO-POR-SITIO-PROPUESTA-2026-10-30.md):
// cada sitio del proyecto lleva SU tipo de cliente y SU tarifa (p. ej. dos negocios PDBT/GDMTO y la casa del dueño en 1F).
// Antes todas las sucursales nacían con el cuestionario del sitio 1 y la casa quedaba en rojo («es tarifa doméstica y el
// proyecto dice comercio»). Un sitio que se queda «igual que el sitio 1» nace EXACTO como antes: con el mismo cuestionario.
import { SITIOS_MAX, TARIFAS_CASA, TARIFAS_NEGOCIO, esMediaTension } from '@cotizador/compartido';
import type { Cotizacion, Cuestionario, ResumenCotizacion, ResumenVersion, Tarifa } from '@cotizador/compartido';
import { conEnfoque, validar } from './cuestionario.ts';
import type { FormaCuestionario } from './cuestionario.ts';

export type TipoSitio = 'comercio' | 'industria' | 'casa';
export const TIPOS_SITIO: readonly TipoSitio[] = ['comercio', 'industria', 'casa'];
export const ETIQUETA_TIPO_SITIO: Readonly<Record<TipoSitio, string>> = { comercio: 'Negocio', industria: 'Industria', casa: 'Casa' };

/** Un renglón de «Varios sitios». tipo/tarifa null = igual que el sitio 1 (lo de antes). */
export interface SitioForma {
  nombre: string;
  tipo: TipoSitio | null;
  tarifa: Tarifa | 'no_se' | null;
}
export type SitiosForma = SitioForma[] | null;

export const sitioVacio = (): SitioForma => ({ nombre: '', tipo: null, tarifa: null });

/** Acepta también la lista de antes (solo nombres). */
export function comoSitio(x: string | SitioForma): SitioForma {
  return typeof x === 'string' ? { ...sitioVacio(), nombre: x } : x;
}

/** ¿Qué le falta a la lista de sitios? null = está bien. */
export function problemaSitios(sitios: readonly (string | SitioForma)[] | null): string | null {
  if (!sitios) return null;
  const nombres = sitios.map((s) => comoSitio(s).nombre.trim());
  if (nombres.length < 2) return 'Un proyecto de varios sitios lleva al menos 2 sucursales.';
  if (nombres.length > SITIOS_MAX) return `Máximo ${SITIOS_MAX} sucursales de una vez.`;
  if (nombres.some((n) => n.length < 2 || n.length > 40)) return 'Escribe el nombre de cada sucursal (2 a 40 letras).';
  const vistos = new Set(nombres.map((n) => n.toLocaleLowerCase('es-MX')));
  if (vistos.size !== nombres.length) return 'Dos sucursales tienen el mismo nombre: cámbiale el nombre a una.';
  return null;
}

/** La lista de tarifas del cuestionario por tipo de cliente (la MISMA en el cuestionario, en cada sitio y al separar). Una
 *  tarifa que ya traía y no es de su lista se queda visible. */
export function opcionesTarifa(para: string | null, actual?: Tarifa | 'no_se' | null): { valor: string; texto: string }[] {
  const o: { valor: string; texto: string }[] = [
    { valor: 'no_se', texto: 'No sé (se lee del recibo)' },
    ...(para === 'casa' || para === null ? TARIFAS_CASA : []).map((t) => ({ valor: t, texto: `${t} · casa` })),
    ...(para !== 'casa' ? TARIFAS_NEGOCIO : []).map((t) => ({ valor: t, texto: `${t} · ${t.startsWith('GDMT') ? 'media tensión' : 'negocio'}` })),
  ];
  if (actual && actual !== 'no_se' && !o.some((x) => x.valor === actual)) o.push({ valor: actual, texto: actual });
  return o;
}

export function tarifaCompatible(tipo: TipoSitio, t: Tarifa | 'no_se'): boolean {
  if (t === 'no_se') return true;
  const deCasa = (TARIFAS_CASA as readonly string[]).includes(t);
  return tipo === 'casa' ? deCasa : !deCasa;
}

/** El tipo y la tarifa que de verdad lleva el sitio: null = los del sitio 1; con otro tipo, la tarifa sin elegir es «No sé». */
export function resolverSitio(f: Pick<FormaCuestionario, 'clienteTipo' | 'tarifa'>, s: SitioForma): { tipo: TipoSitio | null; tarifa: Tarifa | 'no_se' } {
  const tipo = s.tipo ?? f.clienteTipo;
  const tarifa = s.tarifa ?? (tipo === f.clienteTipo ? f.tarifa : 'no_se');
  return { tipo, tarifa };
}

export function igualAlPrincipal(f: Pick<FormaCuestionario, 'clienteTipo' | 'tarifa'>, s: SitioForma): boolean {
  const r = resolverSitio(f, s);
  return r.tipo === f.clienteTipo && r.tarifa === f.tarifa;
}

/** La forma del cuestionario de ESE sitio: la del sitio 1 con su tipo de cliente y su tarifa (conEnfoque, como al elegirlos). */
export function formaDeSitio(f: FormaCuestionario, s: SitioForma): FormaCuestionario {
  if (igualAlPrincipal(f, s)) return f;
  const { tipo, tarifa } = resolverSitio(f, s);
  let n = conEnfoque(f, tipo ? { clienteTipo: tipo } : {});
  n = { ...n, tarifa };
  // Una casa no lleva régimen fiscal ni giro: en el proyecto manda el régimen de la principal (z26, avisoRegimenMultisitio).
  if (tipo === 'casa') n = { ...n, regimen: null, giro: '' };
  // El servicio de CFE del sitio 1 no es el de este sitio: sin contestar, el motor lo deduce (como en el cuestionario). En media
  // tensión la pregunta del transformador es obligatoria: «No sé: lo veo en la visita»; fuera de media tensión no va.
  n = { ...n, servicioFases: null, servicioTension: null, transformador: esMediaTension(tarifa) ? 'visita' : null };
  // Cada cuánto factura sale de SU recibo (la casa bimestral junto a negocios mensuales).
  // Menor (a) del 30-oct: con servicio NUEVO no hay recibo y la casa heredaba el «mensual» del negocio (su ahorro salía al mes).
  // Una casa sin recibo factura cada bimestre, como la cobra CFE; un negocio que sale de una casa: «No sé» (el motor lo decide por
  // su tarifa: la media tensión y el resto de negocio, cada mes).
  if (!f.esNuevo) n = { ...n, facturacion: 'no_se' };
  else if (tipo === 'casa') n = { ...n, facturacion: 'bimestral' };
  else if (f.clienteTipo === 'casa') n = { ...n, facturacion: 'no_se' };
  return n;
}

/** El cuestionario de un sitio que NO es igual al sitio 1, o por qué no se puede. */
export function cuestionarioDeSitio(f: FormaCuestionario, s: SitioForma): { cuestionario: Cuestionario } | { error: string } {
  const { tipo, tarifa } = resolverSitio(f, s);
  if (!tipo) return { error: 'no tiene tipo de cliente: elige Negocio, Industria o Casa.' };
  if (!tarifaCompatible(tipo, tarifa)) return { error: `es ${ETIQUETA_TIPO_SITIO[tipo].toLowerCase()} y la tarifa ${tarifa} no es de ${tipo === 'casa' ? 'casa' : 'negocio'}: cámbiala.` };
  const r = validar(formaDeSitio(f, s), 'notas', true);
  if (r.cuestionario) return { cuestionario: r.cuestionario };
  const errores = Object.entries(r.errores).filter(([, v]) => !!v);
  if (tipo !== 'casa' && f.clienteTipo === 'casa' && errores.some(([k]) => k === 'giro' || k === 'regimen'))
    return { error: `es ${ETIQUETA_TIPO_SITIO[tipo].toLowerCase()} y la sucursal 1 es casa: pon el negocio como sucursal 1 (el giro y el régimen fiscal se piden de la sucursal 1) y la casa como otra sucursal.` };
  return { error: `no se puede crear así: ${errores.map(([, v]) => String(v)).slice(0, 2).join(' ') || 'revisa su tipo y su tarifa.'}` };
}

export interface SitioArmado {
  nombre: string;
  cuestionario: Cuestionario;
}

/** Candado 1 (fallo ruidoso): los cuestionarios de TODOS los sitios, o el error de cuál no pasa. Si uno no pasa no se crea
 *  ninguno. El sitio 1, y todo sitio «igual que el sitio 1», lleva el cuestionario del sitio 1 tal cual (lo de antes). */
export function cuestionariosDeSitios(f: FormaCuestionario, principal: Cuestionario, sitios: readonly SitioForma[]): { sitios: SitioArmado[] } | { error: string } {
  const armados: SitioArmado[] = [];
  for (const [i, s] of sitios.entries()) {
    const nombre = s.nombre.trim();
    if (i === 0 || igualAlPrincipal(f, s)) {
      armados.push({ nombre, cuestionario: principal });
      continue;
    }
    const r = cuestionarioDeSitio(f, s);
    if ('error' in r) return { error: `No se creó ninguna sucursal: «${nombre}» ${r.error}` };
    armados.push({ nombre, cuestionario: r.cuestionario });
  }
  return { sitios: armados };
}

const describir = (q: Cuestionario) => `${q.cliente?.tipo ?? q.tipo} · ${q.tarifa ?? 'no_se'}`;

/** Candado 1, segunda mitad: lo que se pidió de un sitio contra lo que guardó el servidor. null = coincide. */
export function diferenciaSitio(pedido: Cuestionario, guardado: Cuestionario): string | null {
  const firma = (q: Cuestionario) => JSON.stringify([q.tipo, q.cliente?.tipo ?? null, q.tarifa ?? null]);
  return firma(pedido) === firma(guardado) ? null : `se pidió ${describir(pedido)} y quedó ${describir(guardado)}`;
}

const TIPO_PROYECTO: Readonly<Record<string, string>> = { casa: 'Casa', comercio: 'Negocio', industria: 'Industria', baterias: 'Baterías', aislado: 'Aislado' };

/** «Negocio · PDBT · Borrador · calculada» para la lista «Sitios del proyecto». */
export function textoSitio(c: Pick<ResumenCotizacion, 'tipo' | 'tarifa' | 'kwp_recomendada'>, estado: string): string {
  const tipo = c.tipo ? (TIPO_PROYECTO[c.tipo] ?? c.tipo) : 'sin tipo';
  return `${tipo} · ${c.tarifa ?? 'tarifa por leer'} · ${estado} · ${c.kwp_recomendada != null ? 'calculada' : 'falta calcular'}`;
}

/** Candado 3: el último PDF INDIVIDUAL de ESE sitio (nunca el de todas ni el de otra cotización). El de todas sale en el formato
 *  multisitio o, con casas y bimestrales, en el consolidado (ADENDA z68-3): antes el botón «PDF» de la principal bajaba ese. */
export function pdfIndividualDe(id: string, versiones: readonly ResumenVersion[]): ResumenVersion | null {
  return [...versiones].filter((v) => !!v.pdf && v.pdf.formato !== 'multisitio' && v.pdf.formato !== 'consolidado' && v.cotizacion_id === id).sort((a, b) => b.numero - a.numero)[0] ?? null;
}

/** Lo que nombra DESPUÉS de esto no es lo que hay que arreglar en ese sitio: el servidor agrega «(Quedaron fuera del PDF de todas: X (F-1)…)». */
const QUEDARON_FUERA = /\(?Quedaron fuera\b/;

/** Verificador del 2-nov (z48-5, z67-1): «Ir a arreglar» llevaba a Recibos todo lo que no fuera «Saca otra vez el PDF». Con el rojo más
 *  común de la rama («no tiene viáticos: captúralos en sus Costos adicionales») el vendedor llegaba a la misma lista sin el rojo y sin
 *  nada que le dijera qué hacer. Aquí manda lo que PIDE el rojo. El rojo propio de un sitio («no puede sacar su PDF: …») va a Opciones:
 *  su banda de avisos lo enseña con su «Ver el dato». Un texto que no se reconoce va a donde lleva «Editar». Las frases las escribe el
 *  servidor: `sitios.test.ts` truena si alguna deja de estar en sus fuentes. */
export function seccionPorArreglar(textos: readonly string[], c: Pick<ResumenCotizacion, 'tipo'>): 'pdf' | 'costos' | 'opciones' | 'recibos' | 'cuestionario' {
  if (textos.some((t) => t.includes('Saca otra vez el PDF'))) return 'pdf';
  if (textos.some((t) => t.includes('Costos adicionales'))) return 'costos';
  if (textos.some((t) => t.includes('no puede sacar su PDF') || t.includes('Aplicar el precio') || t.includes('revisa sus opciones'))) return 'opciones';
  return c.tipo ? 'recibos' : 'cuestionario';
}

/** Verificador del 1-nov (z48-5, «Ir a arreglarlo»): los sitios que nombra un rojo del PDF de todas, con a dónde lleva arreglar cada
 *  uno. El servidor los nombra siempre «sitio» (FOLIO). */
export function sitiosPorArreglar(textos: readonly string[], sitios: readonly Pick<ResumenCotizacion, 'id' | 'folio' | 'tipo' | 'multisitio'>[]): { id: string; nombre: string; ruta: string }[] {
  const utiles = textos.map((t) => t.split(QUEDARON_FUERA)[0]!);
  return sitios.flatMap((c) => {
    const delSitio = utiles.filter((t) => t.includes(`(${c.folio})`));
    if (!delSitio.length) return [];
    // «no tiene viáticos»: a la forma de sus viáticos, abierta (a 375 el botón de viáticos queda debajo de la barra de abajo). Con «ninguna tiene viáticos»
    // (z46-7, un viático por ciudad) va a la sección, sin forma: el vendedor elige en cuál de las sucursales lo captura.
    const forma = delSitio.some((t) => t.includes('no tiene viáticos')) ? '?agregar=viaticos' : '';
    return [{ id: c.id, nombre: c.multisitio?.sitio ?? c.folio, ruta: `/cotizaciones/${c.id}/${seccionPorArreglar(delSitio, c)}${forma}` }];
  });
}

/** Los rojos que salen iguales en varios sitios y se juntan en UNA frase. Si el servidor reescribe una, `sitios.test.ts` truena: sin eso volverían los párrafos iguales. */
const ROJOS_QUE_SE_JUNTAN: { patron: RegExp; plural: (n: number, m: RegExpMatchArray) => string; detalle?: true }[] = [
  { patron: /^La sucursal «.*» \([^()]*\) es fuera de Hermosillo y no tiene viáticos: captúralos en sus Costos adicionales \((.*)\)\.$/s, plural: (n, m) => `${n} sucursales son fuera de Hermosillo y no tienen viáticos: captúralos en los Costos adicionales de cada una (${m[1]}).` },
  { patron: /^Falta calcular la sucursal «.*» \([^()]*\): ábrela, sube sus recibos y calcula\.$/s, plural: (n) => `Faltan por calcular ${n} sucursales: ábrelas, sube sus recibos y calcula.` },
  { patron: /^La sucursal «.*» \([^()]*\) cambió con los precios o datos de hoy: ábrela, revisa sus opciones y vuelve a intentarlo\.$/s, plural: (n) => `${n} sucursales cambiaron con los precios o datos de hoy: ábrelas, revisa sus opciones y vuelve a intentarlo.` },
  { patron: /^La sucursal «.*» \([^()]*\) tiene un precio que no es el de su vendedor: toca «Aplicar el precio…» en sus Opciones\.$/s, plural: (n) => `${n} sucursales tienen un precio que no es el de su vendedor: toca «Aplicar el precio…» en sus Opciones.` },
  { patron: /^La sucursal «.*» \([^()]*\) no puede sacar su PDF: (.*)$/s, plural: (n, m) => `${n} sucursales no pueden sacar su PDF: ${m[1]}` },
  // Cada una trae sus cifras: la frase va una vez y las cifras se quedan en su detalle, plegado.
  { patron: /^Saca otra vez el PDF de «.*» \([^()]*\): /s, plural: (n) => `Saca otra vez el PDF de ${n} sucursales: cambiaron después de sacarlo.`, detalle: true },
];

export type GrupoDelRojo = { frase: string; sitios: { id: string; nombre: string; ruta: string }[]; detalle: string[] };

/** Verificador del 2-nov (ADENDA l, «Avisos: pocos, importantes y sin repetir»; z60): el rojo repetía su primera falla en el detalle y luego una igual por sitio, con los
 *  botones abajo de todo. Con 5 sitios el primero quedaba bajo la barra de abajo y con 15 en y=1442. Aquí cada falla sale UNA vez, seguida de los botones de sus sitios.
 *  Lo que no se reconoce sale tal cual: nunca se esconde. `inicio` y `cola` son lo que el mensaje dice antes y después de la falla que cita. */
export function rojoDeTodas(mensaje: string, detalles: readonly string[], sitios: Parameters<typeof sitiosPorArreglar>[1]): { inicio: string; grupos: GrupoDelRojo[]; cola: string } {
  const textos = [...new Set(detalles.length ? detalles : [mensaje])];
  const citada = textos.find((t) => mensaje.includes(t));
  const corte = citada ? mensaje.indexOf(citada) : -1;
  const inicio = citada ? mensaje.slice(0, corte) : mensaje;
  const cola = citada ? mensaje.slice(corte + citada.length).trim() : '';
  const enOrden = citada ? [citada, ...textos.filter((t) => t !== citada && !mensaje.includes(t))] : textos;

  const juntos = new Map<string, { regla: (typeof ROJOS_QUE_SE_JUNTAN)[number] | null; m: RegExpMatchArray | null; textos: string[] }>();
  for (const t of enOrden) {
    const regla = ROJOS_QUE_SE_JUNTAN.find((r) => r.patron.test(t)) ?? null;
    const m = regla ? t.match(regla.patron) : null;
    const clave = regla ? `${ROJOS_QUE_SE_JUNTAN.indexOf(regla)}|${m?.[1] ?? ''}` : `texto|${t}`;
    const g = juntos.get(clave);
    if (g) g.textos.push(t);
    else juntos.set(clave, { regla, m, textos: [t] });
  }
  const conBoton = sitiosPorArreglar([mensaje, ...textos], sitios);
  const sinGrupo = new Set(conBoton.map((x) => x.id));
  const nombra = (t: string, id: string) => {
    const folio = sitios.find((c) => c.id === id)?.folio;
    return !!folio && t.split(QUEDARON_FUERA)[0]!.includes(`(${folio})`);
  };
  const grupos = [...juntos.values()].map(({ regla, m, textos: ts }) => {
    const varios = ts.length > 1 && !!regla;
    const de = conBoton.filter((x) => sinGrupo.has(x.id) && ts.some((t) => nombra(t, x.id)));
    de.forEach((x) => sinGrupo.delete(x.id));
    return { frase: varios ? regla.plural(ts.length, m!) : ts[0]!, sitios: de, detalle: varios && regla.detalle ? ts : [] };
  });
  // Un sitio que solo nombra el mensaje (el detalle es técnico): su botón no se pierde.
  if (sinGrupo.size) grupos[0]!.sitios.push(...conBoton.filter((x) => sinGrupo.has(x.id)));
  return { inicio, grupos, cola };
}

/** ¿Este PDF es de varios sitios? Su `contado_recomendada` es lo que cobra UN sitio: nunca se le enseña como el precio de ese PDF. */
export function esPdfDeVariosSitios(formato: string | null | undefined): boolean {
  return formato === 'multisitio' || formato === 'consolidado';
}

/** Lo que el mensaje dice antes de la falla y la falla. Verificador del 2-nov: «(ya salieron).» + «el formato…» se leían pegadas; si la primera ya acabó su frase, la segunda empieza con mayúscula. */
export function unirFrases(inicio: string, frase: string): string {
  const a = inicio.trim();
  if (!a) return frase;
  return /[.!?…]$/.test(a) ? `${a} ${frase.charAt(0).toLocaleUpperCase('es-MX')}${frase.slice(1)}` : `${a} ${frase}`;
}

/** Bloque C: los recibos vivos que la lectura marcó «Otro número de servicio» (los que se pueden separar en otro sitio). */
export function recibosDeOtroServicio(c: Pick<Cotizacion, 'borrador'>): string[] {
  const vivos = new Set(c.borrador.archivos.filter((a) => a.clase === 'recibo' && !a.eliminado).map((a) => a.id));
  return [...new Set((c.borrador.archivos_leidos ?? []).filter((l) => l.estado === 'otro_servicio' && vivos.has(l.archivo_id)).map((l) => l.archivo_id))];
}

/** ¿Esta cotización puede separar recibos en otro sitio? (no en ampliación, aislado ni recorte de punta) */
export function separarAplica(q: Cuestionario): boolean {
  return q.tipo !== 'ampliacion_baterias' && q.tipo !== 'aislado' && !q.recorte_punta;
}

/** El renglón «Sucursales» del resumen antes de crear: nombre y, si no es igual a la 1, su tipo y su tarifa. */
export function textoSitiosResumen(f: Pick<FormaCuestionario, 'clienteTipo' | 'tarifa'>, sitios: readonly SitioForma[]): string {
  return sitios
    .map((x, i) => {
      const n = x.nombre.trim() || '(sin nombre)';
      if (i === 0 || igualAlPrincipal(f, x)) return n;
      const r = resolverSitio(f, x);
      return `${n} (${r.tipo ? ETIQUETA_TIPO_SITIO[r.tipo] : 'sin tipo'} · ${r.tarifa === 'no_se' ? 'tarifa del recibo' : r.tarifa})`;
    })
    .join(', ');
}
